import { useEffect, useMemo, useRef, useState } from "react";
import { useLanguage } from "../context/LanguageContext";

interface BirthDatePickerProps {
  value: string; // "YYYY-MM-DD" veya ""
  onChange: (value: string) => void;
  maxDate?: string; // "YYYY-MM-DD"
  yearSpan?: number; // maxDate yılından geriye kaç yıl listelensin
  autoOpen?: boolean;
  className?: string;
}

const pad = (n: number) => String(n).padStart(2, "0");
const daysInMonth = (year: number, month: number) => new Date(year, month, 0).getDate();

const parseValue = (value: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return { day: null, month: null, year: null };
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
};

function BirthDatePicker({ value, onChange, maxDate, yearSpan = 100, autoOpen = false, className = "" }: BirthDatePickerProps) {
  const { t, lang } = useLanguage();
  const [open, setOpen] = useState(autoOpen);
  const [day, setDay] = useState<number | null>(null);
  const [month, setMonth] = useState<number | null>(null);
  const [year, setYear] = useState<number | null>(null);

  const dayColRef = useRef<HTMLDivElement>(null);
  const monthColRef = useRef<HTMLDivElement>(null);
  const yearColRef = useRef<HTMLDivElement>(null);

  const max = useMemo(() => {
    const p = parseValue(maxDate ?? "");
    if (p.year) return p as { year: number; month: number; day: number };
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
  }, [maxDate]);

  const monthNames = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(lang === "tr" ? "tr-TR" : "en-US", { month: "long" });
    return Array.from({ length: 12 }, (_, i) => fmt.format(new Date(2000, i, 1)));
  }, [lang]);

  const years = useMemo(
    () => Array.from({ length: yearSpan + 1 }, (_, i) => max.year - i),
    [max.year, yearSpan]
  );

  const dayCount = daysInMonth(year ?? 2000, month ?? 1);
  const isMonthDisabled = (m: number) => year === max.year && m > max.month;
  const isDayDisabled = (d: number) => year === max.year && month === max.month && d > max.day;

  // Açılınca mevcut değeri yükle
  useEffect(() => {
    if (!open) return;
    const p = parseValue(value);
    setDay(p.day);
    setMonth(p.month);
    setYear(p.year);
  }, [open]);

  // Ay/yıl değişince geçersiz kalan günü/ayı düzelt
  useEffect(() => {
    if (month !== null && isMonthDisabled(month)) setMonth(max.month);
  }, [year]);

  useEffect(() => {
    if (day === null) return;
    if (day > dayCount) setDay(dayCount);
    else if (isDayDisabled(day)) setDay(max.day);
  }, [month, year, dayCount]);

  // Seçili öğeyi sütunun ortasına kaydır (modal'ı kaydırmamak için scrollIntoView kullanılmaz)
  const centerSelected = (col: HTMLDivElement | null, fallbackIndex?: number) => {
    if (!col) return;
    const item =
      col.querySelector<HTMLElement>(".bdp-item.active") ??
      (fallbackIndex !== undefined ? col.querySelectorAll<HTMLElement>(".bdp-item")[fallbackIndex] : null);
    if (!item) return;
    col.scrollTop = item.offsetTop - col.clientHeight / 2 + item.offsetHeight / 2;
  };

  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      centerSelected(dayColRef.current);
      centerSelected(monthColRef.current);
      // Yıl seçili değilse ~25 yaş civarından başla
      centerSelected(yearColRef.current, Math.min(11, years.length - 1));
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open]);

  const canConfirm = day !== null && month !== null && year !== null;

  const handleConfirm = () => {
    if (!canConfirm) return;
    onChange(`${year}-${pad(month!)}-${pad(day!)}`);
    setOpen(false);
  };

  const display = (() => {
    const p = parseValue(value);
    if (!p.year) return "";
    return `${p.day} ${monthNames[p.month! - 1]} ${p.year}`;
  })();

  const selectionLabel = canConfirm
    ? `${day} ${monthNames[month! - 1]} ${year}`
    : t("datePicker.hint");

  return (
    <>
      <button
        type="button"
        className={`form-control bdp-field text-start ${display ? "" : "bdp-placeholder"} ${className}`}
        onClick={() => setOpen(true)}
      >
        <span>{display || t("datePicker.placeholder")}</span>
        <i className="bi bi-chevron-down bdp-field-caret"></i>
      </button>

      {open && (
        <div
          className="bdp-overlay"
          onClick={() => setOpen(false)}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <div
            className="bdp-sheet"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bdp-header">
              <div className="bdp-header-title">
                <i className="bi bi-cake2 me-2"></i>
                {t("datePicker.title")}
              </div>
              <div className="bdp-header-value">{selectionLabel}</div>
            </div>

            <div className="bdp-columns">
              <div className="bdp-col">
                <div className="bdp-col-label">{t("datePicker.day")}</div>
                <div className="bdp-col-list" ref={dayColRef}>
                  {Array.from({ length: dayCount }, (_, i) => i + 1).map((d) => (
                    <button
                      key={d}
                      type="button"
                      className={`bdp-item ${day === d ? "active" : ""}`}
                      disabled={isDayDisabled(d)}
                      onClick={() => setDay(d)}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bdp-col bdp-col-month">
                <div className="bdp-col-label">{t("datePicker.month")}</div>
                <div className="bdp-col-list" ref={monthColRef}>
                  {monthNames.map((name, i) => (
                    <button
                      key={name}
                      type="button"
                      className={`bdp-item ${month === i + 1 ? "active" : ""}`}
                      disabled={isMonthDisabled(i + 1)}
                      onClick={() => setMonth(i + 1)}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bdp-col">
                <div className="bdp-col-label">{t("datePicker.year")}</div>
                <div className="bdp-col-list" ref={yearColRef}>
                  {years.map((y) => (
                    <button
                      key={y}
                      type="button"
                      className={`bdp-item ${year === y ? "active" : ""}`}
                      onClick={() => setYear(y)}
                    >
                      {y}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="bdp-actions">
              <button type="button" className="btn btn-outline-secondary rounded-pill flex-fill" onClick={() => setOpen(false)}>
                {t("common.cancel")}
              </button>
              <button type="button" className="btn btn-danger rounded-pill flex-fill fw-semibold" onClick={handleConfirm} disabled={!canConfirm}>
                <i className="bi bi-check-lg me-1"></i>
                {t("datePicker.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default BirthDatePicker;
